import { useEffect, useState } from "react";

/** WebOTP credential as returned by the SMS retriever API (not in lib.dom). */
interface OtpCredential extends Credential {
  code: string;
}

/**
 * Subscribes to the WebOTP API and returns the auto-read SMS code, or `null`
 * when unsupported / not yet received.
 *
 * Aborts the pending request on unmount to avoid resolving against a torn-down
 * component (the original implementation only aborted inside its own callbacks).
 */
export const useWebOtpListener = (): string | null => {
  const [otp, setOtp] = useState<string | null>(null);

  useEffect(() => {
    if (!("OTPCredential" in window)) {
      return undefined;
    }

    const controller = new AbortController();
    const request = {
      otp: { transport: ["sms"] },
      signal: controller.signal,
    };

    navigator.credentials
      .get(request as unknown as CredentialRequestOptions)
      .then((credential) => {
        const code = (credential as OtpCredential | null)?.code;
        if (code) {
          setOtp(code);
        }
      })
      .catch((error: unknown) => {
        console.log(error);
      });

    return () => controller.abort();
  }, []);

  return otp;
};
